import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { getQuestionnaires, deleteQuestionnaire, Questionnaire } from '../services/questionnairesService';
import { useToast } from '@/hooks/useToast';

export const QuestionnairesListPage: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getQuestionnaires();
            setQuestionnaires(data);
        } catch (error) {
            toast.error("Erro", "Falha ao carregar questionários.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Tem certeza que deseja excluir este questionário?")) return;
        try {
            await deleteQuestionnaire(id);
            toast.success("Sucesso", "Questionário excluído.");
            loadData();
        } catch (error) {
            toast.error("Erro", "Falha ao excluir.");
        }
    };

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo="/people"
                icon={<ListBulletIcon className="h-6 w-6" aria-hidden />}
                badge="Avaliações"
                title="Questionários"
                description="Modelos de questionários de avaliação com critérios e comportamentos."
                actions={
                    <Button onClick={() => navigate('new')}>
                        <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                        Novo Questionário
                    </Button>
                }
            />

            <div className="flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                            Título
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Tipo
                                        </th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                            <span className="sr-only">Ações</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={3} className="text-center py-4 text-sm text-gray-500">
                                                Carregando...
                                            </td>
                                        </tr>
                                    ) : questionnaires.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-center py-4 text-sm text-gray-500">
                                                Nenhum questionário encontrado.
                                            </td>
                                        </tr>
                                    ) : (
                                        questionnaires.map((q) => (
                                            <tr key={q.id}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                    {q.title}
                                                    <span className="block text-xs text-gray-500 font-normal">{q.description}</span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                                        {q.type === 'PERFORMANCE' ? 'Desempenho' : q.type}
                                                    </span>
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <div className="flex justify-end gap-3">
                                                        <button
                                                            onClick={() => navigate(q.id!)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Editar"
                                                        >
                                                            <PencilIcon className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(q.id!)}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Excluir"
                                                        >
                                                            <TrashIcon className="h-5 w-5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
